import { create } from "mutative";
import { withHistory } from "./withHistory";
import type {
  Body,
  ContactPair,
  Geom,
  GeomKind,
  MaterialUUID,
  MeshUUID,
  UUID,
  BodyUUID,
} from "../../types/scene";
import { generateUuid } from "../../utils/id";
import { logger } from "../../utils/logger";
import { ensureDefaultMatMaterialEntry } from "../../utils/materialDefaults";
import { duplicateSequenceName } from "../../utils/uniqueNames";
import { normalizeGeomSizeForType } from "../../utils/geomTypeEditing";

import type { SceneStoreSetState, SceneStoreGetState } from "../sceneStore.types";
import type { GeomActions, GeomInput } from "./actionTypes";

export const ensureGeomUuid = (geom: Partial<Geom> | Geom): Geom => {
  if (geom.uuid) return geom as Geom;
  return { ...geom, uuid: generateUuid() } as Geom;
};

const ensureGeomArray = (body: Body): Geom[] => {
  if (!Array.isArray(body.geoms)) {
    body.geoms = [];
  }
  return body.geoms;
};

const MIN_GEOM_VALUE = 1e-6;

const collisionFieldKeys = [
  "contactCategory",
  "contactAffinity",
  "contactDimensions",
  "collisionPriority",
  "frictionCoefficients",
  "solverMix",
  "solverReference",
  "solverImpedance",
  "contactMargin",
  "contactGap",
  "mass",
  "density",
] as const;

type MutableGeom = Geom & {
  type?: unknown;
  heightField?: string;
};

type GeomCollisionKey = (typeof collisionFieldKeys)[number];
type VisualOnlyGeom = Geom & Partial<Record<GeomCollisionKey, unknown>>;
type BodyRecord = Record<string, Body>;

const sanitizeGeomSize = (kind: GeomKind | undefined, size: number[]): number[] => {
  return normalizeGeomSizeForType(kind || "sphere", size).map((value: unknown) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0.05;
    return Math.max(MIN_GEOM_VALUE, n);
  });
};

const EDITABLE_GEOM_KINDS = new Set<GeomKind>([
  "plane",
  "hfield",
  "box",
  "sphere",
  "ellipsoid",
  "cylinder",
  "capsule",
]);

const sanitizeEditableGeomKind = (kind: GeomKind): GeomKind => {
  const nextKind = String(kind || "").toLowerCase() as GeomKind;
  return EDITABLE_GEOM_KINDS.has(nextKind) ? nextKind : "sphere";
};

// Normalize a quaternion so exported MJCF never carries a zero-length or NaN rotation.
const sanitizeGeomQuaternion = (
  quaternion: [number, number, number, number]
): [number, number, number, number] => {
  const w = Number(quaternion?.[0]);
  const x = Number(quaternion?.[1]);
  const y = Number(quaternion?.[2]);
  const z = Number(quaternion?.[3]);
  if (![w, x, y, z].every(Number.isFinite)) return [1, 0, 0, 0];
  const len = Math.hypot(w, x, y, z);
  if (len < 1e-8) return [1, 0, 0, 0];
  return [w / len, x / len, y / len, z / len];
};

// Ensure geom position is finite before it reaches MJCF.
const sanitizeGeomPosition = (position: [number, number, number]): [number, number, number] => {
  const out: [number, number, number] = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const n = Number(position?.[i]);
    out[i] = Number.isFinite(n) ? n : 0;
  }
  return out;
};

// MuJoCo requires friction components >= 0 and mass >= 0.
const sanitizeFrictionCoefficients = (
  friction: [number, number, number]
): [number, number, number] => {
  const out: [number, number, number] = [1, 0.005, 0.0001];
  for (let i = 0; i < 3; i++) {
    const n = Number(friction?.[i]);
    out[i] = Number.isFinite(n) ? Math.max(0, n) : out[i];
  }
  return out;
};

const sanitizeGeomMass = (mass: number): number => {
  const n = Number(mass);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n);
};

const sanitizeGeomDensity = (density: number | undefined): number | undefined => {
  if (density == null) return undefined;
  const n = Number(density);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
};

const sanitizeSolverReference = (solverReference: [number, number]): [number, number] => {
  const t = Number(solverReference?.[0]);
  const d = Number(solverReference?.[1]);
  return [
    Math.max(MIN_GEOM_VALUE, Number.isFinite(t) ? t : 0.02),
    Math.max(0, Number.isFinite(d) ? d : 1),
  ];
};

const sanitizeSolverImpedance = (
  solverImpedance: [number, number, number, number, number]
): [number, number, number, number, number] => {
  const dminRaw = Number(solverImpedance?.[0]);
  const dmaxRaw = Number(solverImpedance?.[1]);
  const widthRaw = Number(solverImpedance?.[2]);
  const midRaw = Number(solverImpedance?.[3]);
  const powerRaw = Number(solverImpedance?.[4]);
  const dmin = Math.max(0, Math.min(1, Number.isFinite(dminRaw) ? dminRaw : 0.9));
  const dmax = Math.max(dmin, Math.min(1, Number.isFinite(dmaxRaw) ? dmaxRaw : 0.95));
  const width = Math.max(MIN_GEOM_VALUE, Number.isFinite(widthRaw) ? widthRaw : 0.001);
  const mid = Math.max(0, Math.min(1, Number.isFinite(midRaw) ? midRaw : 0.5));
  const power = Math.max(1, Number.isFinite(powerRaw) ? powerRaw : 2);
  return [dmin, dmax, width, mid, power];
};

/** Same merge as setGeomKindAndSize primary geom — used for paired sync in one history step. */
const applyGeomKindAndSizeMerge = (current: Geom, kind: GeomKind, size: number[]): Geom => {
  const next = ensureGeomUuid({
    ...current,
    kind: sanitizeEditableGeomKind(kind),
    size: sanitizeGeomSize(kind, size) as unknown as [number, number, number],
  });
  delete (next as MutableGeom).type;
  delete (next as MutableGeom).mesh;
  if (next.kind !== "hfield") delete (next as MutableGeom).heightField;
  if (
    next.kind !== "capsule" &&
    next.kind !== "cylinder" &&
    next.kind !== "box" &&
    next.kind !== "ellipsoid"
  ) {
    delete (next as MutableGeom).fromto;
  }
  return next;
};

const applyGeomMeshMerge = (current: Geom, meshUuid: MeshUUID): Geom => {
  const baseSize = Array.isArray(current.size) ? current.size : [1, 1, 1];
  const next = ensureGeomUuid({
    ...current,
    kind: "mesh",
    mesh: meshUuid,
    size: sanitizeGeomSize("mesh", baseSize as number[]) as unknown as [number, number, number],
  });
  delete (next as { heightField?: string }).heightField;
  delete (next as { type?: unknown }).type;
  delete (next as { fromto?: unknown }).fromto;
  return next as Geom;
};

/** Clone geom without `pairedGeomUUID` for store updates. */
const geomWithoutPairingLink = (raw: Partial<Geom> | Geom): Geom => {
  const next = { ...(raw as Geom) };
  delete (next as { pairedGeomUUID?: UUID }).pairedGeomUUID;
  return ensureGeomUuid(next);
};

const clearPairedGeomOnPartner = (
  _draft: unknown,
  body: Body | null,
  partnerUuid: string | undefined
) => {
  if (!body || !partnerUuid) return;
  const geoms = ensureGeomArray(body);
  const pIdx = geoms.findIndex((g) => g && String(g.uuid) === String(partnerUuid));
  if (pIdx === -1) return;
  const p = geoms[pIdx] || {};
  geoms[pIdx] = geomWithoutPairingLink(p as Geom);
};

export const createGeomActions = (
  set: SceneStoreSetState,
  _get: SceneStoreGetState
): GeomActions => ({
  moveGeomToBody: withHistory(
    (sourceBodyUuid: BodyUUID, geomId: string, targetBodyUuid: BodyUUID) => {
      if (!sourceBodyUuid || !targetBodyUuid || sourceBodyUuid === targetBodyUuid) return;

      set((state) =>
        create(state, (draft) => {
          const sourceBody = draft.bodies[sourceBodyUuid] || null;
          const targetBody = draft.bodies[targetBodyUuid] || null;
          if (!sourceBody || !targetBody) {
            logger.warn("[moveGeomToBody] Missing source or target body");
            return;
          }
          const sourceGeoms = ensureGeomArray(sourceBody);
          const idx = findGeomIndex(sourceBody, geomId);
          if (idx === -1) {
            logger.warn("[moveGeomToBody] Geom not found on source body");
            return;
          }
          const moving = sourceGeoms[idx];
          const pairUuid = moving?.pairedGeomUUID;
          if (pairUuid) {
            clearPairedGeomOnPartner(draft, sourceBody, String(pairUuid));
          }
          const [moved] = sourceGeoms.splice(idx, 1);
          if (!moved) return;
          const movedNext = ensureGeomUuid({
            ...(moved as Geom),
          });
          delete (movedNext as { pairedGeomUUID?: UUID }).pairedGeomUUID;
          const targetGeoms = ensureGeomArray(targetBody);
          targetGeoms.push(movedNext);
        })
      );

      try {
        window.dispatchEvent(new CustomEvent("structure-changed"));
      } catch (error) {
        logger.warn(
          "[geomActions] Failed to dispatch structure-changed after moveGeomToBody",
          error
        );
      }
    }
  ),

  removeGeom: withHistory((bodyUuid: BodyUUID, geomId: string) => {
    set((state) =>
      create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = findGeomIndex(targetBody, geomId);
        if (idx === -1) return;
        const removing = geoms[idx];
        const pairUuid = removing?.pairedGeomUUID;
        if (pairUuid) {
          clearPairedGeomOnPartner(draft, targetBody, String(pairUuid));
        }
        geoms.splice(idx, 1);
      })
    );

    // The viewer's rebuild signature tracks bodies and joints but not geoms,
    // so nudge the Scene to rebuild when the geom list changes.
    try {
      window.dispatchEvent(new CustomEvent("structure-changed"));
    } catch (error) {
      logger.warn("[geomActions] Failed to dispatch structure-changed after removeGeom", error);
    }
  }),

  setGeomName: withHistory((bodyUuid: BodyUUID, geomId: string, name: string) =>
    set((state) =>
      create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = findGeomIndex(targetBody, geomId);
        if (idx === -1) return;
        const current = geoms[idx] || {};
        geoms[idx] = ensureGeomUuid({ ...current, name });
      })
    )
  ),

  setGeomMaterial: withHistory((bodyUuid: BodyUUID, geomId: string, materialId: string) =>
    set((state) => {
      const nextState = create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = findGeomIndex(targetBody, geomId);
        if (idx === -1) return;
        const current = geoms[idx] || {};
        geoms[idx] = ensureGeomUuid({
          ...current,
          materials: materialId ? [materialId as MaterialUUID] : [],
        });
      });

      try {
        window.dispatchEvent(new CustomEvent("structure-changed"));
      } catch (error) {
        logger.warn(
          "[geomActions] Failed to dispatch structure-changed after setGeomMaterial",
          error
        );
      }

      return nextState;
    })
  ),

  setGeomPosition: withHistory(
    (bodyUuid: BodyUUID, geomId: string, position: [number, number, number]) =>
      set((state) =>
        create(state, (draft) => {
          const targetBody = draft.bodies[bodyUuid] || null;
          if (!targetBody) return;
          const geoms = ensureGeomArray(targetBody);
          const idx = findGeomIndex(targetBody, geomId);
          if (idx === -1) return;
          const current = geoms[idx] || {};
          const nextPos = sanitizeGeomPosition(position);
          geoms[idx] = ensureGeomUuid({ ...current, position: nextPos });
          const pairUuid = (current as Geom).pairedGeomUUID;
          if (pairUuid) {
            const pIdx = geoms.findIndex((g) => g && String(g.uuid) === String(pairUuid));
            if (pIdx !== -1 && pIdx !== idx) {
              const p = geoms[pIdx] || {};
              geoms[pIdx] = ensureGeomUuid({ ...p, position: nextPos });
            }
          }
        })
      )
  ),

  setGeomOrientation: withHistory(
    (bodyUuid: BodyUUID, geomId: string, quaternion: [number, number, number, number]) =>
      set((state) =>
        create(state, (draft) => {
          const targetBody = draft.bodies[bodyUuid] || null;
          if (!targetBody) return;
          const geoms = ensureGeomArray(targetBody);
          const idx = findGeomIndex(targetBody, geomId);
          if (idx === -1) return;
          const current = geoms[idx] || {};
          const nextQuat = sanitizeGeomQuaternion(quaternion);
          geoms[idx] = ensureGeomUuid({
            ...current,
            orientation: nextQuat,
          });
          const pairUuid = (current as Geom).pairedGeomUUID;
          if (pairUuid) {
            const pIdx = geoms.findIndex((g) => g && String(g.uuid) === String(pairUuid));
            if (pIdx !== -1 && pIdx !== idx) {
              const p = geoms[pIdx] || {};
              geoms[pIdx] = ensureGeomUuid({ ...p, orientation: nextQuat });
            }
          }
        })
      )
  ),

  setGeomSize: withHistory((bodyUuid: BodyUUID, geomId: string, size: number[]) =>
    set((state) =>
      create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = findGeomIndex(targetBody, geomId);
        if (idx === -1) return;
        const current = geoms[idx] || {};
        const sanitized = sanitizeGeomSize((current as Geom).kind, size);
        geoms[idx] = ensureGeomUuid({
          ...current,
          size: sanitized as unknown as [number, number, number],
        });
        const pairUuid = (current as Geom).pairedGeomUUID;
        if (pairUuid) {
          const pIdx = geoms.findIndex((g) => g && String(g.uuid) === String(pairUuid));
          if (pIdx !== -1 && pIdx !== idx) {
            const p = geoms[pIdx] || {};
            const pairedSize = sanitizeGeomSize((p as Geom).kind, sanitized);
            geoms[pIdx] = ensureGeomUuid({
              ...p,
              size: pairedSize as unknown as [number, number, number],
            });
          }
        }
      })
    )
  ),

  setGeomKindAndSize: withHistory(
    (bodyUuid: BodyUUID, geomId: string, kind: GeomKind, size: number[]) =>
      set((state) => {
        const nextState = create(state, (draft) => {
          const targetBody = draft.bodies[bodyUuid] || null;
          if (!targetBody) return;
          const geoms = ensureGeomArray(targetBody);
          const idx = findGeomIndex(targetBody, geomId);
          if (idx === -1) return;
          const current = geoms[idx] || {};
          geoms[idx] = applyGeomKindAndSizeMerge(current as Geom, kind, size);
          const pairUuid = (current as Geom).pairedGeomUUID;
          if (pairUuid) {
            const pIdx = geoms.findIndex((g) => g && String(g.uuid) === String(pairUuid));
            if (pIdx !== -1 && pIdx !== idx) {
              const pCurrent = geoms[pIdx] || {};
              geoms[pIdx] = applyGeomKindAndSizeMerge(pCurrent as Geom, kind, size);
            }
          }
        });

        try {
          window.dispatchEvent(new CustomEvent("structure-changed"));
        } catch (error) {
          logger.warn(
            "[geomActions] Failed to dispatch structure-changed after setGeomKindAndSize",
            error
          );
        }

        return nextState;
      })
  ),

  setGeomMeshReference: withHistory((bodyUuid: BodyUUID, geomId: string, meshUuid: MeshUUID) => {
    if (!bodyUuid || !geomId || !meshUuid) return;

    set((state) => {
      const nextState = create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = findGeomIndex(targetBody, geomId);
        if (idx === -1) return;
        const current = geoms[idx] || {};
        geoms[idx] = applyGeomMeshMerge(current as Geom, meshUuid);
        const pairUuid = (current as Geom).pairedGeomUUID;
        if (pairUuid) {
          const pIdx = geoms.findIndex((g) => g && String(g.uuid) === String(pairUuid));
          if (pIdx !== -1 && pIdx !== idx) {
            const pCurrent = geoms[pIdx] || {};
            geoms[pIdx] = applyGeomMeshMerge(pCurrent as Geom, meshUuid);
          }
        }
      });

      try {
        window.dispatchEvent(new CustomEvent("structure-changed"));
      } catch (error) {
        logger.warn(
          "[geomActions] Failed to dispatch structure-changed after setGeomMeshReference",
          error
        );
      }

      return nextState;
    });
  }),

  setGeomHeightField: withHistory((bodyUuid: BodyUUID, geomId: string, heightField: string) =>
    set((state) => {
      const nextState = create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = findGeomIndex(targetBody, geomId);
        if (idx === -1) return;
        const current = geoms[idx] || {};
        const next = ensureGeomUuid({
          ...current,
          heightField: String(heightField || ""),
        });
        geoms[idx] = next;
      });

      try {
        window.dispatchEvent(new CustomEvent("structure-changed"));
      } catch (error) {
        logger.warn(
          "[geomActions] Failed to dispatch structure-changed after setGeomHeightField",
          error
        );
      }

      return nextState;
    })
  ),

  setGeomFrictionCoefficients: withHistory(
    (bodyUuid: BodyUUID, geomId: string, frictionCoefficients: [number, number, number]) =>
      set((state) =>
        create(state, (draft) => {
          const targetBody = draft.bodies[bodyUuid] || null;
          if (!targetBody) return;
          const geoms = ensureGeomArray(targetBody);
          const idx = findGeomIndex(targetBody, geomId);
          if (idx === -1) return;
          const current = geoms[idx] || {};
          geoms[idx] = ensureGeomUuid({
            ...current,
            frictionCoefficients: sanitizeFrictionCoefficients(frictionCoefficients),
          });
        })
      )
  ),

  setGeomSolverReference: withHistory(
    (bodyUuid: BodyUUID, geomId: string, solverReference: [number, number]) =>
      set((state) =>
        create(state, (draft) => {
          const targetBody = draft.bodies[bodyUuid] || null;
          if (!targetBody) return;
          const geoms = ensureGeomArray(targetBody);
          const idx = findGeomIndex(targetBody, geomId);
          if (idx === -1) return;
          const current = geoms[idx] || {};
          geoms[idx] = ensureGeomUuid({
            ...current,
            solverReference: sanitizeSolverReference(solverReference),
          });
        })
      )
  ),

  setGeomSolverImpedance: withHistory(
    (
      bodyUuid: BodyUUID,
      geomId: string,
      solverImpedance: [number, number, number, number, number]
    ) =>
      set((state) =>
        create(state, (draft) => {
          const targetBody = draft.bodies[bodyUuid] || null;
          if (!targetBody) return;
          const geoms = ensureGeomArray(targetBody);
          const idx = findGeomIndex(targetBody, geomId);
          if (idx === -1) return;
          const current = geoms[idx] || {};
          geoms[idx] = ensureGeomUuid({
            ...current,
            solverImpedance: sanitizeSolverImpedance(solverImpedance),
          });
        })
      )
  ),

  setGeomMass: withHistory((bodyUuid: BodyUUID, geomId: string, mass: number) =>
    set((state) =>
      create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = findGeomIndex(targetBody, geomId);
        if (idx === -1) return;
        const current = geoms[idx] || {};
        geoms[idx] = ensureGeomUuid({ ...current, mass: sanitizeGeomMass(mass) });
      })
    )
  ),

  setGeomDensity: withHistory((bodyUuid: BodyUUID, geomId: string, density: number | undefined) =>
    set((state) =>
      create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = findGeomIndex(targetBody, geomId);
        if (idx === -1) return;
        const current = geoms[idx] || {};
        const sanitized = sanitizeGeomDensity(density);
        const next = ensureGeomUuid({ ...current });
        if (sanitized == null) {
          delete next.density;
        } else {
          next.density = sanitized;
        }
        geoms[idx] = next;
      })
    )
  ),

  setGeomVisualOnly: withHistory((bodyUuid: BodyUUID, geomId: string, visualOnly: boolean) => {
    if (!bodyUuid || !geomId) return;

    set((state) => {
      const nextState = create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = findGeomIndex(targetBody, geomId);
        if (idx === -1) return;

        const current = geoms[idx] as Geom;
        const geomUuid = current?.uuid ? String(current.uuid) : "";
        if (!geomUuid) return;

        const pairUuid = current.pairedGeomUUID;
        if (pairUuid) {
          clearPairedGeomOnPartner(draft, targetBody, String(pairUuid));
        }

        const next = ensureGeomUuid({
          ...current,
          visualOnly,
          materials: Array.isArray(current.materials) ? [...current.materials] : [],
        });
        delete (next as { pairedGeomUUID?: UUID }).pairedGeomUUID;

        if (visualOnly) {
          if (!draft.materials) draft.materials = {};
          const matIds = Array.isArray(next.materials)
            ? next.materials.filter((id) => typeof id === "string" && String(id).trim())
            : [];
          if (matIds.length === 0) {
            next.materials = [ensureDefaultMatMaterialEntry(draft.materials)];
          }
          const visualOnlyGeom = next as VisualOnlyGeom;
          collisionFieldKeys.forEach((key) => {
            delete visualOnlyGeom[key];
          });
          if (Array.isArray(draft.contactPairs)) {
            draft.contactPairs = draft.contactPairs.filter(
              (pair: ContactPair) =>
                String(pair?.geom1) !== geomUuid && String(pair?.geom2) !== geomUuid
            );
          }
        } else {
          next.materials = [];
        }

        geoms[idx] = next;
      });

      try {
        window.dispatchEvent(new CustomEvent("structure-changed"));
      } catch (error) {
        logger.warn(
          "[geomActions] Failed to dispatch structure-changed after setGeomVisualOnly",
          error
        );
      }

      return nextState;
    });
  }),

  pairGeoms: withHistory((bodyUuid: BodyUUID, geomUuidA: UUID, geomUuidB: UUID) => {
    const a = String(geomUuidA || "").trim();
    const b = String(geomUuidB || "").trim();
    if (!a || !b || a === b) return;

    set((state) =>
      create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idxA = geoms.findIndex((g) => g && String(g.uuid) === a);
        const idxB = geoms.findIndex((g) => g && String(g.uuid) === b);
        if (idxA === -1 || idxB === -1) {
          logger.warn("[pairGeoms] One or both geoms not found on body");
          return;
        }
        const gA = geoms[idxA] as Geom;
        const gB = geoms[idxB] as Geom;
        const visA = gA.visualOnly === true;
        const visB = gB.visualOnly === true;
        if (visA === visB) {
          logger.warn("[pairGeoms] Requires one visual-only geom and one collision geom");
          return;
        }

        const unpairPrevious = (geom: Geom) => {
          const prev = geom.pairedGeomUUID;
          if (!prev) return;
          const pi = geoms.findIndex((g) => g && String(g.uuid) === String(prev));
          if (pi === -1) return;
          const pg = geoms[pi] || {};
          geoms[pi] = geomWithoutPairingLink(pg as Geom);
        };

        unpairPrevious(gA);
        unpairPrevious(gB);

        geoms[idxA] = ensureGeomUuid({ ...gA, pairedGeomUUID: b as UUID });
        geoms[idxB] = ensureGeomUuid({ ...gB, pairedGeomUUID: a as UUID });
      })
    );
  }),

  unpairGeom: withHistory((bodyUuid: BodyUUID, geomUuid: UUID) => {
    const id = String(geomUuid || "").trim();
    if (!id) return;

    set((state) =>
      create(state, (draft) => {
        const targetBody = draft.bodies[bodyUuid] || null;
        if (!targetBody) return;
        const geoms = ensureGeomArray(targetBody);
        const idx = geoms.findIndex((g) => g && String(g.uuid) === id);
        if (idx === -1) return;
        const g = geoms[idx] as Geom;
        const partner = g.pairedGeomUUID;
        if (partner) {
          const pIdx = geoms.findIndex((x) => x && String(x.uuid) === String(partner));
          if (pIdx !== -1) {
            const p = geoms[pIdx] || {};
            geoms[pIdx] = geomWithoutPairingLink(p as Geom);
          }
        }
        geoms[idx] = geomWithoutPairingLink(g);
      })
    );
  }),

  addGeom: withHistory((geomInput: GeomInput, parentBodyUuid: BodyUUID): UUID => {
    const geom = { ...geomInput, uuid: geomInput.uuid as UUID | undefined };
    if (!geom.uuid) {
      geom.uuid = ensureGeomUuid(geom).uuid;
    }

    set((state) =>
      create(state, (draft) => {
        const parentBody = draft.bodies[parentBodyUuid] || null;
        if (parentBody) {
          parentBody.geoms = parentBody.geoms || [];
          const nextGeom = { ...geom } as Geom;
          const existingGeomNames = Object.values(draft.bodies as BodyRecord).flatMap((body) =>
            Array.isArray(body?.geoms)
              ? body.geoms.map((existingGeom) => existingGeom?.name).filter(Boolean)
              : []
          );
          nextGeom.name = duplicateSequenceName(
            nextGeom.name || `geom_${nextGeom.kind || "geom"}`,
            existingGeomNames,
            "_",
            "geom"
          );
          if (nextGeom.visualOnly === true) {
            const matIds = Array.isArray(nextGeom.materials)
              ? nextGeom.materials.filter((id) => typeof id === "string" && String(id).trim())
              : [];
            if (matIds.length === 0) {
              if (!draft.materials) draft.materials = {};
              const defaultMatId = ensureDefaultMatMaterialEntry(draft.materials);
              nextGeom.materials = [defaultMatId];
            }
          }
          parentBody.geoms.push(ensureGeomUuid(nextGeom));
        } else {
          logger.warn(`[addGeom] Parent body ${parentBodyUuid} not found`);
        }
      })
    );

    // The viewer's rebuild signature tracks bodies and joints but not geoms,
    // so nudge the Scene to rebuild when a new geom is added.
    try {
      window.dispatchEvent(new CustomEvent("structure-changed"));
    } catch (error) {
      logger.warn("[geomActions] Failed to dispatch structure-changed after addGeom", error);
    }
    return geom.uuid as UUID;
  }),
});

function findGeomIndex(body: Body, geomId: string): number {
  const geoms = Array.isArray(body.geoms) ? body.geoms : [];
  if (!geoms || geoms.length === 0) return -1;

  // Accept composite ids like "bodyUuid:geomUuid" and strip the body hint
  const idStr = String(geomId);
  const sep = idStr.indexOf(":");
  const geomUuid = sep > 0 ? idStr.slice(sep + 1) : idStr;

  // UUID-only matching — no name/mesh fallbacks
  const idx = geoms.findIndex((g) => g && String(g.uuid) === geomUuid);
  return idx;
}
